import { ErrorComponent } from '@karnov/kui-react'

import { useCallback, useEffect, useRef, useState } from 'react'

import { useConversationsQuery } from 'api/conversations/queries'
import { useActiveConversationId } from 'hooks/useActiveConversationId'
import { useDebouncedSearchValue } from 'hooks/useDebouncedSearchValue'
import { useSetActiveConversationId } from 'hooks/useSetActiveConversationId'
import { useTriggerEvent } from 'hooks/useTriggerEvent'
import { useTranslation } from 'services/i18n'
import { useClosePanel } from 'stores/usePanelsStateStore'
import { useClearPendingUserMessage } from 'stores/usePendingUserMessageStateStore'
import { useClearPromptInput } from 'stores/usePromptInputStore'
import { useIsXlOrAbove } from 'stores/useScreenSizeStore'

import { Conversation } from 'types/Api'

import { ConversationHistorySkeletonLoader } from './ConversationHistorySkeletonLoader'
import { DeleteConversationModal } from './components/ConversationItem/DeleteConversationModal'
import { ConversationPagination } from './components/ConversationPagination'
import { PinnedConversations } from './components/PinnedConversations'
import { UnpinnedConversations } from './components/UnpinnedConversations'

import type { FC } from 'react'

import type { TriggerEvent } from 'api/events/types'

export const GroupedConversations: FC = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'conversation_history',
  })

  const clearPendingUserMessage = useClearPendingUserMessage()

  const clearPromptInput = useClearPromptInput()

  const [conversationSelectedForDeletion, setConversationSelectedForDeletion]
    = useState<Conversation | null>(null)

  const triggerEvent = useTriggerEvent()

  const isXlOrAbove = useIsXlOrAbove()

  const debouncedSearchValue = useDebouncedSearchValue()

  const handleTriggerEvent: TriggerEvent = useCallback(
    params => triggerEvent(params),
    [triggerEvent],
  )

  const { activeConversationId } = useActiveConversationId()

  const { setActiveConversationId } = useSetActiveConversationId()

  const trackedSearchValueRef = useRef('')

  const handleClearActiveConversationId = useCallback(() => {
    clearPendingUserMessage()

    setActiveConversationId()
  }, [clearPendingUserMessage, setActiveConversationId])

  const handleActivateConversation = useCallback(
    (conversationId: string) => {
      clearPendingUserMessage()

      if (conversationId && conversationId !== activeConversationId) {
        clearPromptInput()
      }

      setActiveConversationId(conversationId)
    },
    [
      activeConversationId,
      clearPendingUserMessage,
      clearPromptInput,
      setActiveConversationId,
    ],
  )

  const {
    data,
    isError,
    isPending,
    isFetching,
    isSuccess,
  } = useConversationsQuery({
    query: debouncedSearchValue,
  })

  const pinnedConversations = data?.pages[0].pinned || []

  const unpinnedConversations = data?.pages.flatMap(page => page?.conversations) || []

  const isEmptySearchResults = !pinnedConversations.length && !unpinnedConversations.length && Boolean(debouncedSearchValue)

  const closePanel = useClosePanel()

  useEffect(() => {
    if (debouncedSearchValue && trackedSearchValueRef.current !== debouncedSearchValue) {
      trackedSearchValueRef.current = debouncedSearchValue
      triggerEvent({ name: 'kai_conversation_search_result_shown' })
    }
  }, [debouncedSearchValue, isSuccess, triggerEvent])

  const handleSelectConversation = useCallback(
    (conversationId: string) => {
      if (conversationId !== activeConversationId) {
        handleActivateConversation(conversationId)

        handleTriggerEvent({
          name: 'kai_conversation_clicked',
          properties: {
            conversationId,
            filter: debouncedSearchValue || undefined,
          },
        })
        if (!isXlOrAbove) closePanel('conversations_menu')
      }
    },
    [
      activeConversationId,
      closePanel,
      debouncedSearchValue,
      handleActivateConversation,
      handleTriggerEvent,
      isXlOrAbove,
    ],
  )

  const handleClickDeleteConversation = useCallback(
    (conversation: Omit<Conversation, 'messages'>) => {
      handleTriggerEvent({
        name: 'kai_delete_conversation_clicked',
        properties: {
          conversationId: conversation.id,
        },
      })
      setConversationSelectedForDeletion(conversation)
    },
    [handleTriggerEvent, setConversationSelectedForDeletion],
  )

  const handleCloseModal = useCallback(() => setConversationSelectedForDeletion(null), [setConversationSelectedForDeletion])

  if (isError) {
    return (
      <ErrorComponent
        description={t('error.description')}
        title={t('error.title')}
      />
    )
  }

  if (isPending) {
    return <ConversationHistorySkeletonLoader />
  }

  if (isEmptySearchResults) {
    return <span className="mx-2 my-1 text-sm">{t('empty_search_in_conversation', { searchTerm: debouncedSearchValue })}</span>
  }

  return (
    <>
      <DeleteConversationModal
        isOpen={Boolean(conversationSelectedForDeletion)}
        conversation={conversationSelectedForDeletion}
        onClearActiveConversationId={handleClearActiveConversationId}
        onClose={handleCloseModal}
      />
      <PinnedConversations
        activeConversationId={activeConversationId}
        conversations={pinnedConversations}
        onClickDeleteConversation={handleClickDeleteConversation}
        onSelectConversation={handleSelectConversation}
        onTriggerEvent={handleTriggerEvent}
      />
      <UnpinnedConversations
        activeConversationId={activeConversationId}
        conversations={unpinnedConversations}
        onClickDeleteConversation={handleClickDeleteConversation}
        onSelectConversation={handleSelectConversation}
        onTriggerEvent={handleTriggerEvent}
      />
      {isFetching && <ConversationHistorySkeletonLoader />}
      <ConversationPagination />
    </>
  )
}
